import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStickyNote, faSave } from '@fortawesome/free-solid-svg-icons';
import { API } from 'aws-amplify';
import * as mutations from '../../graphql/mutations';

/**
 * Always-visible, inline-editable Project Notes.
 *
 * Notes capture issues that may impact the project and are surfaced to Harmony™ AI
 * (Harmony) when it scopes project details. Unlike the full project-edit modal,
 * this card stays visible and has its own Save button so notes can be updated
 * continuously while the project is ongoing.
 *
 * Persists via updateProject (notes field only). `onUpdated` syncs the parent's
 * project state with the server response.
 */
const ProjectNotesCard = ({ project, onUpdated }) => {
  const [notes, setNotes] = useState(project?.notes || '');
  const [isSaving, setIsSaving] = useState(false);

  // Re-sync the draft when the project (re)loads or its saved notes change.
  useEffect(() => {
    setNotes(project?.notes || '');
  }, [project?.id, project?.notes]);

  const dirty = (notes || '') !== (project?.notes || '');

  const handleSave = async () => {
    if (!project?.id || isSaving) return;
    setIsSaving(true);
    try {
      const result = await API.graphql({
        query: mutations.updateProject,
        variables: { input: { id: project.id, notes, _version: project._version } },
      });
      if (onUpdated) onUpdated(result.data.updateProject);
    } catch (error) {
      console.error('Error saving project notes:', error);
      alert('Failed to save notes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="border-0 shadow-sm mb-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faStickyNote} className="me-2 text-primary" />
            Project Notes
          </h5>
          <Button size="sm" variant="primary" onClick={handleSave} disabled={!dirty || isSaving}>
            {isSaving ? (
              <Spinner as="span" size="sm" animation="border" />
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="me-1" />
                Save
              </>
            )}
          </Button>
        </div>
        <p className="text-muted small mb-2">
          Notes about issues that may impact this project (e.g. supplier delays, staffing, pending approvals).
          Harmony&trade; AI reads these when answering about the project. Your organization&rsquo;s OCP always takes precedence.
        </p>
        <Form.Control
          as="textarea"
          rows={4}
          value={notes}
          placeholder="Add notes about issues that may impact this project&hellip;"
          onChange={(e) => setNotes(e.target.value)}
        />
      </Card.Body>
    </Card>
  );
};

export default ProjectNotesCard;
